<template>
  <content-dialog
    v-model="isOpen"
    title="Select Checklist Templates"
    width="500px"
    fullscreen-mobile
    @show="loadTemplates"
    @hide="onHide"
  >
    <!-- Search filter -->
    <q-input
      v-model="search"
      dense
      outlined
      clearable
      placeholder="Search templates..."
      class="q-mx-md q-mt-md"
    >
      <template #prepend>
        <q-icon name="mdi-magnify" />
      </template>
    </q-input>

    <!-- Template list with checkboxes -->
    <q-list
      v-if="!loading && filteredTemplates.length > 0"
      class="q-mt-sm"
      separator
    >
      <q-expansion-item
        v-for="template in filteredTemplates"
        :key="template.id"
        dense-toggle
        expand-icon-class="text-grey"
      >
        <template #header>
          <q-item-section
            side
            class="q-pr-sm"
            @click.stop
          >
            <q-checkbox
              v-model="selectedTemplateIds"
              :val="template.id"
            />
          </q-item-section>
          <q-item-section>
            <q-item-label>
              {{ template.name }}
              <span
                v-if="template.version"
                class="text-caption text-grey-6 q-ml-xs"
              >v{{ template.version }}</span>
            </q-item-label>
            <q-item-label caption>
              {{ (template.items || []).length }} {{ (template.items || []).length === 1 ? 'item' : 'items' }}
            </q-item-label>
          </q-item-section>
          <q-item-section
            side
            class="q-pr-sm"
            @click.stop
          >
            <q-btn
              flat
              dense
              round
              icon="mdi-pencil"
              color="grey-7"
              @click.stop="editTemplate(template)"
            >
              <q-tooltip>Edit template</q-tooltip>
            </q-btn>
          </q-item-section>
        </template>

        <q-list dense class="bg-grey-1 q-py-xs">
          <q-item
            v-for="(item, idx) in (template.items || [])"
            :key="idx"
            class="q-pl-xl"
          >
            <q-item-section avatar>
              <q-icon
                name="mdi-circle-small"
                size="md"
                color="grey-7"
              />
            </q-item-section>
            <q-item-section>
              <q-item-label>{{ item.text || '(empty item)' }}</q-item-label>
            </q-item-section>
            <q-item-section side>
              <div class="row q-gutter-xs">
                <q-chip
                  v-if="item.is_required"
                  dense
                  size="sm"
                  color="blue-grey-1"
                  text-color="blue-grey-9"
                  icon="mdi-asterisk"
                  label="Required"
                />
                <q-chip
                  v-if="item.requires_photo"
                  dense
                  size="sm"
                  color="blue-grey-1"
                  text-color="blue-grey-9"
                  icon="mdi-camera"
                  label="Photo"
                />
                <q-chip
                  v-if="item.requires_invoice"
                  dense
                  size="sm"
                  color="blue-grey-1"
                  text-color="blue-grey-9"
                  icon="mdi-file-document-outline"
                  label="Invoice"
                />
                <q-chip
                  v-if="item.requires_quotation"
                  dense
                  size="sm"
                  color="blue-grey-1"
                  text-color="blue-grey-9"
                  icon="mdi-file-document-edit-outline"
                  label="Quote"
                />
                <q-chip
                  v-for="form in (item.required_forms || [])"
                  :key="form.id"
                  dense
                  size="sm"
                  color="blue-grey-1"
                  text-color="blue-grey-9"
                  icon="mdi-clipboard-text-outline"
                  :label="form.title"
                />
              </div>
            </q-item-section>
          </q-item>
          <q-item v-if="(template.items || []).length === 0">
            <q-item-section class="text-grey text-italic q-pl-xl">
              No items in this template
            </q-item-section>
          </q-item>
        </q-list>
      </q-expansion-item>
    </q-list>

    <!-- Empty state -->
    <div
      v-else-if="!loading && filteredTemplates.length === 0"
      class="text-caption text-grey text-center q-pa-lg"
    >
      {{ search ? 'No templates match your search' : 'No checklist templates available' }}
    </div>

    <template #bottom>
      <q-toolbar>
        <q-btn
          flat
          no-caps
          color="primary"
          icon="mdi-plus"
          label="Create Template"
          @click="createTemplate"
        />
        <q-space />
        <q-btn
          color="green"
          label="Confirm"
          icon-right="done"
          :disable="selectedTemplateIds.length === 0"
          @click="confirm"
        />
      </q-toolbar>
    </template>

    <EditDialog
      ref="editDialog"
      @saved="onTemplateSaved"
    />
  </content-dialog>
</template>

<script>
import EditDialog from '../EditDialog'

export default {
  components: {
    EditDialog
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    selectedIds: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:modelValue', 'selected'],
  data () {
    return {
      loading: false,
      templates: [],
      selectedTemplateIds: [],
      search: ''
    }
  },
  computed: {
    isOpen: {
      get () {
        return this.modelValue
      },
      set (val) {
        this.$emit('update:modelValue', val)
      }
    },
    filteredTemplates () {
      if (!this.search) {
        return this.templates
      }
      const query = this.search.toLowerCase()
      return this.templates.filter(t => t.name.toLowerCase().includes(query))
    }
  },
  methods: {
    async loadTemplates (force = false) {
      if (!force) {
        // Pre-check the already selected IDs on initial open
        this.selectedTemplateIds = [...this.selectedIds]

        if (this.templates.length > 0) {
          return
        }
      }

      this.loading = true
      const { status, payload } = await this.$get('v1/checklist-templates')
      this.loading = false
      if (status === 'success') {
        this.templates = payload.templates || []
      }
    },
    confirm () {
      const selected = this.templates.filter(t => this.selectedTemplateIds.includes(t.id))
      this.$emit('selected', selected)
      this.isOpen = false
    },
    createTemplate () {
      this.$refs.editDialog.open({
        name: null,
        items: []
      })
    },
    editTemplate (template) {
      this.$refs.editDialog.open(template)
    },
    async onTemplateSaved (payload) {
      // Editing a template creates a NEW version row (new id), so track selection by
      // reference_id and re-map to the current version ids after reloading.
      const savedRef = payload && payload.template ? payload.template.reference_id : null
      const selectedRefs = this.templates
        .filter(t => this.selectedTemplateIds.includes(t.id))
        .map(t => t.reference_id)

      await this.loadTemplates(true)

      this.selectedTemplateIds = this.templates
        .filter(t => selectedRefs.includes(t.reference_id))
        .map(t => t.id)

      // Auto-select a brand-new template (a create, version 1) so the user can confirm it.
      this.templates.forEach(t => {
        if (t.reference_id === savedRef && t.version === 1 && !this.selectedTemplateIds.includes(t.id)) {
          this.selectedTemplateIds.push(t.id)
        }
      })
    },
    onHide () {
      this.search = ''
    }
  }
}
</script>
